import { priceSaid } from '../../lib/format';
import {
  addBooking,
  ConfirmDialog,
  deleteBooking,
  Dialog,
  type Booking,
  type Plan,
  type PlanStop,
} from '@trippath/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BookingDialog } from './BookingDialog';
import { dayRange, plusDays } from '../../lib/countryFacts';
import { ChangeBooking } from '../stop/StopEdits';

interface Props {
  plan: Plan;
  /** The stop as the plan says it now. */
  stop: PlanStop;
  onClose: () => void;
  onSaved: (plan: Plan) => void;
}

/**
 * A stop's beds in a dialog: each stay booked for it, by its name, nights and price, to change
 * or remove; another added.
 */
export function StayBookings({ plan, stop, onClose, onSaved }: Props) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [open, setOpen] = useState<'add' | Booking | null>(null);
  const [removing, setRemoving] = useState<Booking | null>(null);
  const [failed, setFailed] = useState(false);
  const now = stop;
  const stays = now.bookings.filter((booking) => booking.kind === 'stay');
  const kept = (next: Plan) => {
    setOpen(null);
    onSaved(next);
  };
  return (
    <>
      <Dialog
        open={open === null}
        title={`${now.city} · ${dayRange(now.start, plusDays(now.start, now.days), lang)}`}
        onClose={onClose}
      >
        {stays.length === 0 ? (
          <p className="quiet">{t('stays.none')}</p>
        ) : (
          <ul className="stay-bookings">
            {stays.map((booking) => (
              <li key={booking.id}>
                <span className="stay-booking-name">
                  <strong>{booking.name}</strong>
                  <span className="quiet">
                    {t('stays.nights', { count: booking.nights })} ·{' '}
                    {priceSaid(booking.price, booking.paid, lang)}
                  </span>
                </span>
                <button
                  type="button"
                  className="button button-small"
                  aria-label={t('stays.changeOne', { name: booking.name })}
                  onClick={() => {
                    setOpen(booking);
                  }}
                >
                  {t('stays.change')}
                </button>
                <button
                  type="button"
                  className="button button-small button-quiet"
                  aria-label={t('stays.removeOne', { name: booking.name })}
                  onClick={() => {
                    setRemoving(booking);
                  }}
                >
                  {t('stays.remove')}
                </button>
              </li>
            ))}
          </ul>
        )}
        {failed && (
          <p className="form-failed" role="alert">
            {t('plan.failed')}
          </p>
        )}
        <div className="dialog-actions">
          <button
            type="button"
            className="button button-primary"
            onClick={() => {
              setOpen('add');
            }}
          >
            {t('stays.add')}
          </button>
        </div>
      </Dialog>
      {open === 'add' && (
        <BookingDialog
          plan={plan}
          draft={{
            stop: now.id,
            kind: 'stay',
            name: '',
            day: now.start,
            nights: now.days,
            price: null,
            currency: null,
            pay: 'paid',
            reference: null,
            link: null,
          }}
          save={(input) => addBooking(plan.trip.id, input)}
          onClose={() => {
            setOpen(null);
          }}
          onSaved={kept}
        />
      )}
      {open !== null && open !== 'add' && (
        <ChangeBooking
          plan={plan}
          stop={now}
          booking={open}
          onClose={() => {
            setOpen(null);
          }}
          onSaved={kept}
        />
      )}
      {removing && (
        <ConfirmDialog
          open
          title={t('stays.removeQuestion', { name: removing.name })}
          body={t('stays.removeBody')}
          confirmLabel={t('stays.remove')}
          onCancel={() => {
            setRemoving(null);
          }}
          onConfirm={() => {
            setRemoving(null);
            setFailed(false);
            deleteBooking(plan.trip.id, removing.id).then(onSaved, () => {
              setFailed(true);
            });
          }}
        />
      )}
    </>
  );
}
